import { useCallback, useRef, useState } from 'react';
import { getDiscoverVibeExplainers } from '../../api/discover';
import { composeVibeWhy, hasUnmatchedCriteria } from '../../helpers/discover_helpers';

// Raw fields the backend interprets into tier labels (never quoted as numbers).
const buildMetrics = c => ({
  instagramCount: c.instagramCount || 0,
  youtubeCount: c.youtubeCount || 0,
  tiktokCount: c.tiktokCount || 0,
  monthlyVolume: c.monthlyVolume || 0,
  averageOrderAmount: c.averageOrderAmount || 0,
});

// On-demand LLM prose for vibe full-match creators. Each page of newcomers is
// sent in one batched request so the model sees the whole grid and varies
// phrasing across it; the backend chunks the page into parallel batches. A
// per-query in-memory cache (session-only) dedups across infinite-scroll appends.
export const useVibeExplainers = () => {
  // `${queryKey}:${creatorId}` -> { status: 'loading' | 'done' | 'error', text? }.
  const cacheRef = useRef(new Map());
  // Active query = cache namespace; swapping it invalidates in-flight requests.
  const queryKeyRef = useRef(null);

  // Re-render the consumer as prose lands (getExplainer reads refs).
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick(t => t + 1), []);

  const reset = useCallback(() => {
    cacheRef.current = new Map();
    queryKeyRef.current = null;
    bump();
  }, [bump]);

  // Queue uncached full-match creators (in rank order) into one batched request;
  // idempotent across pages — only newcomers for the active query are sent.
  const requestFor = useCallback((creators, { brandId, vibeQuery, disambiguation } = {}) => {
    if (!brandId || !vibeQuery || !Array.isArray(creators)) return;
    // Degraded query split → why is the generic line; nothing worth polishing.
    if (disambiguation?.degraded === true) return;

    const queryKey = vibeQuery;
    if (queryKeyRef.current !== queryKey) {
      cacheRef.current = new Map();
      queryKeyRef.current = queryKey;
    }

    const batch = [];
    for (const c of creators) {
      // Full matches only — partial matches keep the templated line.
      if (!c || !c.evidence || hasUnmatchedCriteria(c.evidence)) continue;
      const key = `${queryKey}:${c.id}`;
      if (cacheRef.current.has(key)) continue;
      const why = composeVibeWhy(c.evidence, disambiguation);
      if (!why.length) continue;
      cacheRef.current.set(key, { status: 'loading' });
      batch.push({
        creatorId: c.id,
        why,
        creatorName: c.name,
        topCategoryAffinityName: c.topCategoryAffinityName,
        userBrandSegment: c.userBrandSegment,
        categoryAffinity: c.categoryAffinity,
        metrics: buildMetrics(c),
        // Titles (contract v5); capped to bound prompt tokens.
        products: (c.products || []).map(p => p.title).filter(Boolean).slice(0, 8),
      });
    }
    if (!batch.length) return;
    bump();

    getDiscoverVibeExplainers(brandId, { vibeQuery, creators: batch })
      .then(resp => {
        if (queryKeyRef.current !== queryKey) return; // stale — newer search took over
        const byId = new Map(
          (resp?.success && Array.isArray(resp.explainers) ? resp.explainers : [])
            .map(e => [e.creatorId, e.explainer]),
        );
        for (const item of batch) {
          const text = byId.get(item.creatorId);
          cacheRef.current.set(
            `${queryKey}:${item.creatorId}`,
            text ? { status: 'done', text } : { status: 'error' },
          );
        }
        bump();
      })
      .catch(() => {
        if (queryKeyRef.current !== queryKey) return;
        for (const item of batch) {
          cacheRef.current.set(`${queryKey}:${item.creatorId}`, { status: 'error' });
        }
        bump();
      });
  }, [bump]);

  const getExplainer = useCallback(creatorId => {
    const qk = queryKeyRef.current;
    if (qk == null) return undefined;
    return cacheRef.current.get(`${qk}:${creatorId}`);
  }, []);

  return { requestFor, getExplainer, reset };
};
